import { useState } from "react";
import { useSignal, effect } from "@preact/signals-react";
import { useNavigate } from "react-router";
import { FormattedMessage as FM } from "react-intl";

import Layout from "components/Layout/ViewLayout";
import BoardTable from "componentsv2/table/BoardTable";
import ModalTable from "componentsv2/table/ModalTable";
import Button from "componentsv2/button/Button";
import DatePicker from "componentsv2/form/DatePicker";
import Dropdown from "componentsv2/form/Dropdown";
import proxy from "api/proxy";
import { layout as layoutAudit } from "../ModelStaffAudit";
import { getSections, resolveEmployeeIds } from "../common";
import SectionNoData from "components/Tools/SectionNoData";

const model = "staff.access.audit";
const method = "audit_shift_assistance";

function ViewAudit() {
  const navigate = useNavigate();
  const currentView = useSignal({ name: "staff.assistance.audit_title" });
  const [records, setRecords] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const selectedSection = useSignal(null);
  const selectedEmployee = useSignal(null);
  const selectedStart = useSignal(null);
  const selectedEnd = useSignal(null);

  effect(() => {
    if (!currentView.value) navigate(-1);
  });

  async function handleSearch() {
    if (!selectedStart.value) {
      setError("staff.assistance.audit.require_dates");
      return;
    }
    const startDate = selectedStart.value;
    const endDate = selectedEnd.value || startDate;
    const args = {
      section: selectedSection.value?.id,
      employee: selectedEmployee.value?.id,
      start_date: `${startDate} 00:00:00`,
      end_date: `${endDate} 23:59:59`,
    };
    setLoading(true);
    setError(null);
    const { data, error } = await proxy.methodCall({
      model,
      method,
      args: [args],
    });
    setLoading(false);
    if (error) {
      setError(typeof error === "string" ? error : error?.message);
      return;
    }
    const _records = (data || []).map((rec, index) => ({
      ...rec,
      id: rec.access_id || index + 1,
      audit_start: startDate,
      audit_end: endDate,
    }));
    const resolved = await resolveEmployeeIds(_records);
    setRecords(resolved);
  }

  const attrsSection = {
    label: "staff.access.section",
    name: "section",
    type: "many2one",
    model: "staff.section",
    searchable: true,
    preloaded: true,
    preOptions: getSections,
    field: {},
  };

  const attrsEmployee = {
    label: "staff.access.employee",
    name: "employee",
    type: "many2one",
    model: "company.employee",
    field: {},
  };

  const attrsStart = {
    label: "staff.assistance.audit.start_date",
    name: "start_date",
    field: {},
  };

  const attrsEnd = {
    label: "staff.assistance.audit.end_date",
    name: "end_date",
    field: {},
  };

  return (
    <Layout
      currentView={currentView}
      title="staff.assistance.audit_title"
      style="px-5!"
    >
      <style>
        {`
      #table-header {
        display: none;
      }
    `}
      </style>
      <div className="flex flex-col w-full bg-neutral-100">
        <div className="flex flex-wrap items-end gap-3 mb-4 pl-2">
          <div className="w-full md:w-52">
            <DatePicker {...attrsStart} data={selectedStart} />
          </div>
          <div className="w-full md:w-52">
            <DatePicker {...attrsEnd} data={selectedEnd} />
          </div>
          <div className="w-full md:w-60">
            <Dropdown {...attrsSection} data={selectedSection} />
          </div>
          <div className="w-full md:w-60">
            <Dropdown {...attrsEmployee} data={selectedEmployee} />
          </div>

          <div className="w-full md:w-40">
            <Button
              content="staff.assistance.audit.search"
              onClick={handleSearch}
              color="amber"
              height="h-10"
              size="w-full"
              style="mb-1.5"
            />
          </div>
        </div>
        {error && (
          <p className="text-rose-500 text-sm mb-2">
            <FM id={error} defaultMessage={error} />
          </p>
        )}
        {records === null ? (
          <SectionNoData text={<FM id="staff.assistance.audit.empty" />} />
        ) : records.length === 0 ? (
          <SectionNoData text={<FM id="staff.assistance.audit.no_data" />} />
        ) : (
          <BoardTable layout={layoutAudit()} records={records} />
        )}
      </div>
      <ModalTable />
    </Layout>
  );
}

export default ViewAudit;
